import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useDropzone } from 'react-dropzone';
import { HexColorPicker } from 'react-colorful';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Globe, Layout, Image, FileText, Settings, Eye, Save, Palette, GripVertical,
  Plus, Trash2, Upload, ExternalLink, PawPrint, Search, MapPin, Phone, Mail,
  ArrowLeft, Check, X, Edit, Copy, RefreshCw, Sparkles, Wand2, Loader2, Layers
} from 'lucide-react';
import SectionBuilder from '@/components/website/SectionBuilder';

const WebsiteBuilder = ({ user, onBack }) => {
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('design');
  const [selectedPage, setSelectedPage] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  
  // Animals for publish
  const [animals, setAnimals] = useState([]);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [publishType, setPublishType] = useState('adoptable');
  
  // Add page dialog
  const [showAddPageDialog, setShowAddPageDialog] = useState(false);
  const [newPage, setNewPage] = useState({ name: '', slug: '', type: 'custom' });
  
  // Forms
  const [availableForms, setAvailableForms] = useState([]);
  
  // AI Assistant
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiContentType, setAiContentType] = useState('hero');
  const [aiCustomPrompt, setAiCustomPrompt] = useState('');
  const [aiGeneratedContent, setAiGeneratedContent] = useState('');

  // AI Content Generation
  const generateAIContent = async (contentType, customPrompt = '') => {
    setAiGenerating(true);
    setAiGeneratedContent('');
    try {
      const response = await axios.post('/website/ai-generate', {
        content_type: contentType,
        prompt: customPrompt,
        context: {
          organization_name: website?.title || user.organization_name || user.business_name || user.clinic_name || user.name
        }
      });
      setAiGeneratedContent(response.data.content);
      toast.success('Content generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setAiGenerating(false);
    }
  };

  const applyAIContent = (content, contentType) => {
    if (!content) return;
    
    if (contentType === 'hero') {
      // Parse HEADLINE: | TAGLINE: format
      const parts = content.split('|').map(p => p.trim());
      const headline = parts[0]?.replace('HEADLINE:', '').trim();
      const tagline = parts[1]?.replace('TAGLINE:', '').trim();
      if (headline) {
        setWebsite(prev => ({ ...prev, hero_title: headline, tagline: tagline || prev.tagline }));
      }
    } else if (contentType === 'about' || contentType === 'description') {
      setWebsite(prev => ({ ...prev, description: content }));
    }
    
    toast.success('Content applied to your website!');
    setShowAIDialog(false);
    setAiGeneratedContent('');
  };

  useEffect(() => {
    fetchWebsite();
    if (user.role === 'shelter') {
      fetchAnimals();
    }
  }, [user.role]);

  const fetchWebsite = async () => {
    try {
      const response = await axios.get('/website/my');
      if (response.data) {
        setWebsite(response.data);
        if (response.data.pages?.length > 0) {
          setSelectedPage(response.data.pages[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch website:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnimals = async () => {
    try {
      const response = await axios.get('/shelter/animals');
      setAnimals(response.data || []);
    } catch (error) {
      console.error('Failed to fetch animals:', error);
    }
  };

  const createWebsite = async () => {
    try {
      setSaving(true);
      const response = await axios.post('/website/create', {
        title: user.organization_name || user.business_name || user.clinic_name || user.name
      });
      setWebsite(response.data);
      if (response.data.pages?.length > 0) {
        setSelectedPage(response.data.pages[0]);
      }
      toast.success('Website created! Start customizing.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create website');
    } finally {
      setSaving(false);
    }
  };

  const saveWebsite = async () => {
    if (!website) return;
    try {
      setSaving(true);
      // Send only the fields that should be updated
      const updateData = {
        title: website.title,
        tagline: website.tagline,
        description: website.description,
        hero_title: website.hero_title,
        logo_url: website.logo_url,
        hero_image_url: website.hero_image_url,
        favicon_url: website.favicon_url,
        theme: website.theme,
        pages: website.pages,
        sections: website.sections,
        navigation: website.navigation,
        footer: website.footer,
        contact: website.contact,
        social_links: website.social_links,
        seo: website.seo
      };
      
      const response = await axios.put('/website/update', updateData);
      setWebsite(response.data);
      toast.success('Website saved successfully!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save website');
    } finally {
      setSaving(false);
    }
  };

  const publishWebsite = async () => {
    try {
      setSaving(true);
      const response = await axios.put('/website/publish');
      setWebsite({ ...website, is_published: true });
      toast.success(`Website published! Visit: ${response.data.url}`);
    } catch (error) {
      toast.error('Failed to publish website');
    } finally {
      setSaving(false);
    }
  };

  const unpublishWebsite = async () => {
    try {
      await axios.put('/website/unpublish');
      setWebsite({ ...website, is_published: false });
      toast.success('Website unpublished');
    } catch (error) {
      toast.error('Failed to unpublish');
    }
  };

  // Image upload
  const onDrop = useCallback(async (acceptedFiles, imageType = 'general') => {
    const file = acceptedFiles[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', imageType);

    try {
      const response = await axios.post('/website/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (imageType === 'logo') {
        setWebsite({ ...website, logo_url: response.data.url });
      } else if (imageType === 'hero') {
        setWebsite({ ...website, hero_image_url: response.data.url });
      }
      
      toast.success('Image uploaded!');
      return response.data.url;
    } catch (error) {
      toast.error('Failed to upload image');
      return null;
    }
  }, [website]);

  const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps } = useDropzone({
    onDrop: (files) => onDrop(files, 'logo'),
    accept: { 'image/*': [] },
    maxFiles: 1
  });

  const { getRootProps: getHeroRootProps, getInputProps: getHeroInputProps } = useDropzone({
    onDrop: (files) => onDrop(files, 'hero'),
    accept: { 'image/*': [] },
    maxFiles: 1
  });

  // Page management
  const addPage = async () => {
    if (!newPage.name) return;
    try {
      const response = await axios.post('/website/pages', newPage);
      setWebsite({
        ...website,
        pages: [...website.pages, response.data]
      });
      setShowAddPageDialog(false);
      setNewPage({ name: '', slug: '', type: 'custom' });
      toast.success('Page added!');
    } catch (error) {
      toast.error('Failed to add page');
    }
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await axios.delete(`/website/pages/${pageId}`);
      setWebsite({
        ...website,
        pages: website.pages.filter(p => p.page_id !== pageId)
      });
      toast.success('Page deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cannot delete this page');
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(website.pages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setWebsite({ ...website, pages: items });

    try {
      await axios.put('/website/pages/reorder', items.map(p => p.page_id));
    } catch (error) {
      console.error('Failed to save page order');
    }
  };

  // Publish animal to website
  const handlePublishAnimal = async () => {
    if (!selectedAnimal) return;
    try {
      await axios.put(`/shelter/animals/${selectedAnimal.animal_id}/publish-to-website`, {
        publish_type: publishType,
        description: selectedAnimal.description || '',
        photos: selectedAnimal.photos || []
      });
      toast.success(`${selectedAnimal.name} published to ${publishType === 'adoptable' ? 'Adoptable Pets' : 'Found Pets'} page!`);
      setShowPublishDialog(false);
      setSelectedAnimal(null);
      fetchAnimals();
    } catch (error) {
      toast.error('Failed to publish animal');
    }
  };

  const handleUnpublishAnimal = async (animalId) => {
    try {
      await axios.put(`/shelter/animals/${animalId}/unpublish-from-website`);
      toast.success('Animal removed from website');
      fetchAnimals();
    } catch (error) {
      toast.error('Failed to unpublish');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p>Loading Website Builder...</p>
        </div>
      </div>
    );
  }

  if (!website) {
    return (
      <div className="min-h-screen bg-[#fdfbf7]">
        <header className="bg-primary text-white">
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={onBack} className="text-white hover:bg-white/10">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <Globe className="w-8 h-8" />
              <h1 className="text-2xl font-bold">Website Builder</h1>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-6 py-16">
          <Card className="max-w-2xl mx-auto text-center">
            <CardHeader>
              <Globe className="w-16 h-16 text-primary mx-auto mb-4" />
              <CardTitle className="text-2xl">Create Your Website</CardTitle>
              <CardDescription>
                Build a beautiful website for your {user.role === 'shelter' ? 'rescue organization' : user.role === 'breeder' ? 'breeding business' : 'veterinary clinic'}.
                {user.role === 'shelter' && ' Pets you publish will automatically appear on your Adoptable Pets and Found Pets pages.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 mb-6 text-left">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Drag-and-drop page builder</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Custom subdomain (yourname.petregistry.com)</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Auto-sync pets from your profile</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Attach adoption & contact forms</span>
                </div>
              </div>
              <Button onClick={createWebsite} disabled={saving} size="lg" className="bg-primary hover:bg-primary/90">
                {saving ? 'Creating...' : 'Create My Website'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      {/* Header */}
      <header className="bg-primary text-white sticky top-0 z-50">
        <div className="container mx-auto px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onBack} className="text-white hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Globe className="w-6 h-6" />
            <div>
              <h1 className="text-lg font-bold">Website Builder</h1>
              <p className="text-xs text-white/70">{website.subdomain}.petregistry.com</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={website.is_published ? 'default' : 'secondary'} className="bg-white/20">
              {website.is_published ? '● Live' : '○ Draft'}
            </Badge>
            <Button variant="outline" size="sm" onClick={() => setPreviewMode(!previewMode)} className="text-white border-white hover:bg-white/10">
              <Eye className="w-4 h-4 mr-1" />Preview
            </Button>
            {website.is_published && (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-white border-white hover:bg-white/10"
                onClick={() => window.open(`/site/${website.subdomain}`, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-1" />View Live
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={saveWebsite} disabled={saving} className="text-white border-white hover:bg-white/10">
              <Save className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save'}
            </Button>
            {website.is_published ? (
              <Button variant="destructive" size="sm" onClick={unpublishWebsite}>
                Unpublish
              </Button>
            ) : (
              <Button size="sm" onClick={publishWebsite} className="bg-green-600 hover:bg-green-700">
                Publish
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* PREVIEW MODE */}
      {previewMode && website && (
        <div className="fixed inset-0 z-50 bg-white overflow-auto">
          <div className="bg-stone-900 text-white p-2 flex items-center justify-between sticky top-0 z-10">
            <span className="text-sm">Preview Mode - {website.subdomain}.petregistry.com</span>
            <Button size="sm" variant="outline" onClick={() => setPreviewMode(false)} className="text-white border-white">
              <X className="w-4 h-4 mr-1" /> Close Preview
            </Button>
          </div>
          
          {/* Simulated Website */}
          <div style={{ backgroundColor: website.theme?.backgroundColor || '#fdfbf7' }}>
            {/* Header */}
            <header style={{ backgroundColor: website.theme?.primaryColor || '#4F46E5' }} className="text-white">
              <div className="container mx-auto px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {website.logo_url ? (
                    <img src={website.logo_url} alt="Logo" className="h-10" />
                  ) : (
                    <PawPrint className="w-8 h-8" />
                  )}
                  <h1 className="text-xl font-bold">{website.title || 'My Rescue'}</h1>
                </div>
                <nav className="flex gap-4">
                  {website.pages?.map(page => (
                    <a key={page.slug} href="#" className="hover:underline">{page.name}</a>
                  ))}
                </nav>
              </div>
            </header>

            {/* Hero */}
            <div 
              className="relative h-80 flex items-center justify-center text-white text-center"
              style={{ 
                backgroundImage: website.hero_image_url ? `url(${website.hero_image_url})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="absolute inset-0 bg-black/40"></div>
              <div className="relative z-10 px-6">
                <h2 className="text-4xl font-bold mb-4">{website.hero_title || 'Welcome to Our Rescue'}</h2>
                <p className="text-xl mb-6">{website.tagline || 'Finding loving homes for pets in need'}</p>
                <Button size="lg" style={{ backgroundColor: website.theme?.secondaryColor || '#10B981' }}>
                  View Available Pets
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="container mx-auto px-6 py-12">
              <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: website.theme?.primaryColor || '#4F46E5' }}>
                Pets for Adoption
              </h3>
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                {animals.filter(a => a.published_to_website && a.website_publish_type === 'adoptable').slice(0, 8).map(animal => (
                  <Card key={animal.animal_id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="aspect-square bg-stone-100 flex items-center justify-center">
                      {animal.photos?.length > 0 ? (
                        <img src={animal.photos[0].url || animal.photos[0]} alt={animal.name} className="w-full h-full object-cover" />
                      ) : (
                        <PawPrint className="w-16 h-16 text-stone-300" />
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h4 className="font-semibold">{animal.name}</h4>
                      <p className="text-sm text-stone-600">{animal.species} • {animal.breed}</p>
                      <p className="text-sm font-medium mt-2" style={{ color: website.theme?.primaryColor }}>
                        ${animal.adoption_fee || 0}
                      </p>
                    </CardContent>
                  </Card>
                ))}
                {animals.filter(a => a.published_to_website && a.website_publish_type === 'adoptable').length === 0 && (
                  <div className="col-span-full text-center py-12 text-stone-500">
                    <PawPrint className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>No pets published for adoption yet</p>
                    <p className="text-sm mt-2">Go to the Pets tab to publish animals to your website.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <footer style={{ backgroundColor: website.theme?.primaryColor || '#4F46E5' }} className="text-white py-8">
              <div className="container mx-auto px-6 text-center">
                <p className="mb-2">{website.title || 'My Rescue'}</p>
                {website.contact?.phone && <p><Phone className="w-4 h-4 inline mr-2" />{website.contact.phone}</p>}
                {website.contact?.email && <p><Mail className="w-4 h-4 inline mr-2" />{website.contact.email}</p>}
                {website.contact?.address && <p><MapPin className="w-4 h-4 inline mr-2" />{website.contact.address}</p>}
                <p className="mt-4 text-sm opacity-70">Powered by International Pet Registry</p>
              </div>
            </footer>
          </div>
        </div>
      )}

      <div className="container mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="design"><Palette className="w-4 h-4 mr-2" />Design</TabsTrigger>
            <TabsTrigger value="sections"><Layers className="w-4 h-4 mr-2" />Sections</TabsTrigger>
            <TabsTrigger value="pages"><Layout className="w-4 h-4 mr-2" />Pages</TabsTrigger>
            <TabsTrigger value="pets"><PawPrint className="w-4 h-4 mr-2" />Pets</TabsTrigger>
            <TabsTrigger value="forms"><FileText className="w-4 h-4 mr-2" />Forms</TabsTrigger>
            <TabsTrigger value="settings"><Settings className="w-4 h-4 mr-2" />Settings</TabsTrigger>
          </TabsList>

          {/* DESIGN TAB */}
          <TabsContent value="design" className="space-y-6">
            {/* AI Assistant Button */}
            <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-stone-800">AI Content Assistant</h3>
                      <p className="text-sm text-stone-600">Generate compelling content for your website with AI</p>
                    </div>
                  </div>
                  <Button onClick={() => setShowAIDialog(true)} className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
                    <Wand2 className="w-4 h-4 mr-2" /> Generate Content
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Branding */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Branding</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Website Title</Label>
                    <Input
                      value={website.title || ''}
                      onChange={(e) => setWebsite({ ...website, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Tagline</Label>
                    <Input
                      value={website.tagline || ''}
                      onChange={(e) => setWebsite({ ...website, tagline: e.target.value })}
                      placeholder="A short slogan or description"
                    />
                  </div>
                  <div>
                    <Label>Logo</Label>
                    <div {...getLogoRootProps()} className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary">
                      <input {...getLogoInputProps()} />
                      {website.logo_url ? (
                        <div className="relative">
                          <img src={website.logo_url} alt="Logo" className="h-16 mx-auto" />
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            className="absolute top-0 right-0 h-6 w-6 p-0"
                            onClick={(e) => { e.stopPropagation(); setWebsite({...website, logo_url: null}); }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="text-stone-500">
                          <Upload className="w-8 h-8 mx-auto mb-2" />
                          <p className="text-sm">Drag & drop or click to upload logo</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Hero Image</Label>
                    <div {...getHeroRootProps()} className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary">
                      <input {...getHeroInputProps()} />
                      {website.hero_image_url ? (
                        <div className="relative">
                          <img src={website.hero_image_url} alt="Hero" className="h-32 w-full object-cover rounded" />
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            className="absolute top-2 right-2 h-8 w-8 p-0"
                            onClick={(e) => { e.stopPropagation(); setWebsite({...website, hero_image_url: null}); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="text-stone-500">
                          <Image className="w-8 h-8 mx-auto mb-2" />
                          <p className="text-sm">Drag & drop hero image</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Colors */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Theme Colors</CardTitle>
                  <p className="text-sm text-stone-500">Customize your website color scheme</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Preset Color Palettes */}
                  <div>
                    <Label className="mb-2 block">Quick Color Presets</Label>
                    <div className="grid grid-cols-5 gap-2">
                      {[
                        { name: 'Forest', colors: { primaryColor: '#166534', secondaryColor: '#22c55e', accentColor: '#86efac', backgroundColor: '#f0fdf4', textColor: '#14532d' } },
                        { name: 'Ocean', colors: { primaryColor: '#1e40af', secondaryColor: '#3b82f6', accentColor: '#93c5fd', backgroundColor: '#eff6ff', textColor: '#1e3a8a' } },
                        { name: 'Sunset', colors: { primaryColor: '#c2410c', secondaryColor: '#f97316', accentColor: '#fdba74', backgroundColor: '#fff7ed', textColor: '#7c2d12' } },
                        { name: 'Berry', colors: { primaryColor: '#9333ea', secondaryColor: '#a855f7', accentColor: '#d8b4fe', backgroundColor: '#faf5ff', textColor: '#581c87' } },
                        { name: 'Rose', colors: { primaryColor: '#be123c', secondaryColor: '#f43f5e', accentColor: '#fda4af', backgroundColor: '#fff1f2', textColor: '#881337' } },
                      ].map((preset) => (
                        <Button
                          key={preset.name}
                          variant="outline"
                          size="sm"
                          className="flex flex-col items-center p-2 h-auto"
                          onClick={() => setWebsite({
                            ...website,
                            theme: { ...website.theme, ...preset.colors }
                          })}
                        >
                          <div className="flex gap-0.5 mb-1">
                            {Object.values(preset.colors).slice(0, 3).map((color, i) => (
                              <div key={i} className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
                            ))}
                          </div>
                          <span className="text-xs">{preset.name}</span>
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* All Color Options */}
                  <div className="space-y-4">
                    <Label className="mb-2 block font-medium">All Color Settings</Label>
                    {[
                      { key: 'primaryColor', label: 'Primary Color', desc: 'Main brand color' },
                      { key: 'secondaryColor', label: 'Secondary Color', desc: 'Buttons, links' },
                      { key: 'accentColor', label: 'Accent Color', desc: 'Highlights, badges' },
                      { key: 'backgroundColor', label: 'Background', desc: 'Page background' },
                      { key: 'textColor', label: 'Text Color', desc: 'Main text' },
                      { key: 'headingColor', label: 'Heading Color', desc: 'Titles, headers' },
                      { key: 'headerBgColor', label: 'Header Background', desc: 'Navigation bar' },
                      { key: 'headerTextColor', label: 'Header Text', desc: 'Nav text' },
                      { key: 'footerBgColor', label: 'Footer Background', desc: 'Footer area' },
                      { key: 'footerTextColor', label: 'Footer Text', desc: 'Footer text' },
                    ].map((colorItem) => (
                      <div key={colorItem.key} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                        <div>
                          <Label className="font-medium">{colorItem.label}</Label>
                          <p className="text-xs text-stone-500">{colorItem.desc}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-10 h-10 rounded border cursor-pointer shadow-sm"
                            style={{ backgroundColor: website.theme?.[colorItem.key] || '#166534' }}
                            onClick={() => setShowColorPicker(showColorPicker === colorItem.key ? null : colorItem.key)}
                          />
                          <Input
                            value={website.theme?.[colorItem.key] || ''}
                            onChange={(e) => setWebsite({
                              ...website,
                              theme: { ...website.theme, [colorItem.key]: e.target.value }
                            })}
                            className="w-28 font-mono text-sm"
                            placeholder="#000000"
                          />
                        </div>
                        {showColorPicker === colorItem.key && (
                          <div className="absolute z-10 mt-40 right-8">
                            <div className="bg-white p-2 rounded-lg shadow-lg border">
                              <HexColorPicker
                                color={website.theme?.[colorItem.key] || '#166534'}
                                onChange={(color) => setWebsite({
                                  ...website,
                                  theme: { ...website.theme, [colorItem.key]: color }
                                })}
                              />
                              <Button 
                                size="sm" 
                                className="w-full mt-2"
                                onClick={() => setShowColorPicker(null)}
                              >
                                Done
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div>
                    <Label>Font Family</Label>
                    <Select
                      value={website.theme?.font_family || 'Manrope'}
                      onValueChange={(v) => setWebsite({
                        ...website,
                        theme: { ...website.theme, font_family: v }
                      })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Manrope">Manrope</SelectItem>
                        <SelectItem value="Inter">Inter</SelectItem>
                        <SelectItem value="Poppins">Poppins</SelectItem>
                        <SelectItem value="Open Sans">Open Sans</SelectItem>
                        <SelectItem value="Roboto">Roboto</SelectItem>
                        <SelectItem value="Lato">Lato</SelectItem>
                        <SelectItem value="Montserrat">Montserrat</SelectItem>
                        <SelectItem value="Nunito">Nunito</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* SECTIONS TAB */}
          <TabsContent value="sections" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Homepage Sections</CardTitle>
                <CardDescription>Build your homepage by adding and customizing sections</CardDescription>
              </CardHeader>
              <CardContent>
                <SectionBuilder 
                  sections={website.sections || []}
                  onUpdate={(sections) => setWebsite({ ...website, sections })}
                  organizationName={website.title || user?.organization_name || 'Our Organization'}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* PAGES TAB */}
          <TabsContent value="pages" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-lg">Website Pages</CardTitle>
                    <CardDescription>Drag to reorder. System pages cannot be deleted.</CardDescription>
                  </div>
                  <Button onClick={() => setShowAddPageDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />Add Page
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="pages">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                        {website.pages?.map((page, index) => (
                          <Draggable key={page.page_id} draggableId={page.page_id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`flex items-center justify-between p-3 border rounded-lg ${snapshot.isDragging ? 'shadow-lg bg-white' : 'bg-stone-50'}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div {...provided.dragHandleProps}>
                                    <GripVertical className="w-5 h-5 text-stone-400 cursor-grab" />
                                  </div>
                                  <div>
                                    <p className="font-medium">{page.name}</p>
                                    <p className="text-xs text-stone-500">/{page.slug || ''}</p>
                                  </div>
                                  {page.is_system && (
                                    <Badge variant="outline" className="text-xs">System</Badge>
                                  )}
                                  {page.type === 'adoptable_pets' && (
                                    <Badge className="bg-green-100 text-green-800 text-xs">Auto-synced</Badge>
                                  )}
                                  {page.type === 'found_pets' && (
                                    <Badge className="bg-orange-100 text-orange-800 text-xs">Auto-synced</Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button size="sm" variant="ghost">
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  {!page.is_system && (
                                    <Button size="sm" variant="ghost" onClick={() => deletePage(page.page_id)}>
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PETS TAB (for Rescues) */}
          <TabsContent value="pets" className="space-y-6">
            {user.role === 'shelter' ? (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Publish Pets to Website</CardTitle>
                    <CardDescription>
                      Select pets to publish to your Adoptable Pets or Found Pets page. They will appear automatically.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Available to publish */}
                      <div>
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <PawPrint className="w-4 h-4" /> Your Animals ({animals.filter(a => !a.published_to_website).length})
                        </h4>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {animals.filter(a => !a.published_to_website).map((animal) => (
                            <div key={animal.animal_id} className="flex items-center justify-between p-3 border rounded-lg">
                              <div className="flex items-center gap-3">
                                {animal.photos?.[0] ? (
                                  <img src={animal.photos[0].url || animal.photos[0]} alt="" className="w-12 h-12 rounded object-cover" />
                                ) : (
                                  <div className="w-12 h-12 bg-stone-200 rounded flex items-center justify-center">
                                    <PawPrint className="w-6 h-6 text-stone-400" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium">{animal.name}</p>
                                  <p className="text-xs text-stone-500">{animal.species} • {animal.breed}</p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedAnimal(animal);
                                  setShowPublishDialog(true);
                                }}
                              >
                                Publish
                              </Button>
                            </div>
                          ))}
                          {animals.filter(a => !a.published_to_website).length === 0 && (
                            <p className="text-center text-stone-500 py-4">All animals are published!</p>
                          )}
                        </div>
                      </div>

                      {/* Published */}
                      <div>
                        <h4 className="font-medium mb-3 flex items-center gap-2 text-green-700">
                          <Check className="w-4 h-4" /> Published ({animals.filter(a => a.published_to_website).length})
                        </h4>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {animals.filter(a => a.published_to_website).map((animal) => (
                            <div key={animal.animal_id} className="flex items-center justify-between p-3 border border-green-200 bg-green-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                {animal.photos?.[0] ? (
                                  <img src={animal.photos[0].url || animal.photos[0]} alt="" className="w-12 h-12 rounded object-cover" />
                                ) : (
                                  <div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center">
                                    <PawPrint className="w-6 h-6 text-green-600" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium">{animal.name}</p>
                                  <Badge variant="outline" className="text-xs">
                                    {animal.website_publish_type === 'adoptable' ? 'Adoptable' : 'Found'}
                                  </Badge>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUnpublishAnimal(animal.animal_id)}
                              >
                                Remove
                              </Button>
                            </div>
                          ))}
                          {animals.filter(a => a.published_to_website).length === 0 && (
                            <p className="text-center text-stone-500 py-4">No pets published yet</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-8 text-center">
                  <PawPrint className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">Pet publishing is available for rescue organizations.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* FORMS TAB */}
          <TabsContent value="forms" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Website Forms</CardTitle>
                <CardDescription>Enable forms on your website for visitors to contact you.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { type: 'adoption', name: 'Adoption Application', roles: ['shelter'] },
                    { type: 'foster', name: 'Foster Application', roles: ['shelter'] },
                    { type: 'volunteer', name: 'Volunteer Application', roles: ['shelter'] },
                    { type: 'contact', name: 'Contact Form', roles: ['shelter', 'breeder', 'vet'] },
                    { type: 'waitlist', name: 'Waitlist Signup', roles: ['breeder'] },
                    { type: 'appointment', name: 'Appointment Request', roles: ['vet'] },
                  ].filter(f => f.roles.includes(user.role)).map((form) => {
                    const isEnabled = website.forms_enabled?.some(f => f.form_type === form.type);
                    return (
                      <div key={form.type} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">{form.name}</p>
                          <p className="text-xs text-stone-500">
                            {isEnabled ? '✓ Enabled on website' : 'Not enabled'}
                          </p>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={async (checked) => {
                            try {
                              if (checked) {
                                await axios.put('/website/attach-form', { form_type: form.type });
                                setWebsite({
                                  ...website,
                                  forms_enabled: [...(website.forms_enabled || []), { form_type: form.type, enabled: true }]
                                });
                              } else {
                                await axios.put('/website/detach-form', { form_type: form.type });
                                setWebsite({
                                  ...website,
                                  forms_enabled: website.forms_enabled?.filter(f => f.form_type !== form.type) || []
                                });
                              }
                              toast.success(`${form.name} ${checked ? 'enabled' : 'disabled'}`);
                            } catch (error) {
                              toast.error('Failed to update form');
                            }
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Domain Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Subdomain</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={website.subdomain || ''}
                        onChange={(e) => setWebsite({ ...website, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      />
                      <span className="text-stone-500">.petregistry.com</span>
                    </div>
                  </div>
                  <div>
                    <Label>Custom Domain (Optional)</Label>
                    <Input
                      value={website.custom_domain || ''}
                      onChange={(e) => setWebsite({ ...website, custom_domain: e.target.value })}
                      placeholder="www.yourdomain.com"
                    />
                    <p className="text-xs text-stone-500 mt-1">
                      Point your domain CNAME to: sites.petregistry.com
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Footer Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Copyright Text</Label>
                    <Input
                      value={website.footer?.copyright_text || ''}
                      onChange={(e) => setWebsite({
                        ...website,
                        footer: { ...website.footer, copyright_text: e.target.value }
                      })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Show Social Links</Label>
                    <Switch
                      checked={website.footer?.show_social ?? true}
                      onCheckedChange={(checked) => setWebsite({
                        ...website,
                        footer: { ...website.footer, show_social: checked }
                      })}
                    />
                  </div>
                  {website.footer?.show_social && (
                    <div className="space-y-2">
                      <Input
                        placeholder="Facebook URL"
                        value={website.footer?.social_links?.facebook || ''}
                        onChange={(e) => setWebsite({
                          ...website,
                          footer: {
                            ...website.footer,
                            social_links: { ...website.footer?.social_links, facebook: e.target.value }
                          }
                        })}
                      />
                      <Input
                        placeholder="Instagram URL"
                        value={website.footer?.social_links?.instagram || ''}
                        onChange={(e) => setWebsite({
                          ...website,
                          footer: {
                            ...website.footer,
                            social_links: { ...website.footer?.social_links, instagram: e.target.value }
                          }
                        })}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">SEO Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Meta Description</Label>
                    <Textarea
                      value={website.description || ''}
                      onChange={(e) => setWebsite({ ...website, description: e.target.value })}
                      placeholder="A brief description for search engines..."
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Links</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {website.is_published && (
                    <a
                      href={`https://${website.subdomain}.petregistry.com`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 border rounded-lg hover:bg-stone-50"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>View Live Website</span>
                    </a>
                  )}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`https://${website.subdomain}.petregistry.com`);
                      toast.success('URL copied!');
                    }}
                    className="flex items-center gap-2 p-3 border rounded-lg hover:bg-stone-50 w-full"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy Website URL</span>
                  </button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Page Dialog */}
      <Dialog open={showAddPageDialog} onOpenChange={setShowAddPageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Page</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Page Name</Label>
              <Input
                value={newPage.name}
                onChange={(e) => setNewPage({ ...newPage, name: e.target.value })}
                placeholder="e.g., Our Story"
              />
            </div>
            <div>
              <Label>URL Slug</Label>
              <Input
                value={newPage.slug}
                onChange={(e) => setNewPage({ ...newPage, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                placeholder="e.g., our-story"
              />
            </div>
            <div>
              <Label>Page Type</Label>
              <Select value={newPage.type} onValueChange={(v) => setNewPage({ ...newPage, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom Content</SelectItem>
                  <SelectItem value="gallery">Photo Gallery</SelectItem>
                  <SelectItem value="faq">FAQ</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPageDialog(false)}>Cancel</Button>
            <Button onClick={addPage}>Add Page</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Animal Dialog */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish to Website</DialogTitle>
          </DialogHeader>
          {selectedAnimal && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-3 bg-stone-50 rounded-lg">
                {selectedAnimal.photos?.[0] ? (
                  <img src={selectedAnimal.photos[0].url || selectedAnimal.photos[0]} alt="" className="w-16 h-16 rounded object-cover" />
                ) : (
                  <div className="w-16 h-16 bg-stone-200 rounded flex items-center justify-center">
                    <PawPrint className="w-8 h-8 text-stone-400" />
                  </div>
                )}
                <div>
                  <p className="font-semibold">{selectedAnimal.name}</p>
                  <p className="text-sm text-stone-600">{selectedAnimal.species} • {selectedAnimal.breed}</p>
                </div>
              </div>
              <div>
                <Label>Publish to Page</Label>
                <Select value={publishType} onValueChange={setPublishType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="adoptable">
                      <div className="flex items-center gap-2">
                        <PawPrint className="w-4 h-4 text-green-600" />
                        Adoptable Pets Page
                      </div>
                    </SelectItem>
                    <SelectItem value="found">
                      <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-orange-600" />
                        Found Pets Page
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm text-stone-500">
                {publishType === 'adoptable' 
                  ? 'This pet will appear on your Adoptable Pets page for potential adopters.'
                  : 'This pet will appear on your Found Pets page to help find its owner.'}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>Cancel</Button>
            <Button onClick={handlePublishAnimal} className="bg-green-600 hover:bg-green-700">
              Publish to Website
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Content Generator Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI Content Generator
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Content Type</Label>
              <Select value={aiContentType} onValueChange={setAiContentType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hero">Hero Section (Headline + Tagline)</SelectItem>
                  <SelectItem value="about">About Us Section</SelectItem>
                  <SelectItem value="description">Meta Description (SEO)</SelectItem>
                  <SelectItem value="adoption_info">Adoption Information</SelectItem>
                  <SelectItem value="contact_intro">Contact Page Introduction</SelectItem>
                  <SelectItem value="custom">Custom Prompt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {aiContentType === 'custom' && (
              <div>
                <Label>Your Prompt</Label>
                <Textarea
                  value={aiCustomPrompt}
                  onChange={(e) => setAiCustomPrompt(e.target.value)}
                  placeholder="Describe what content you'd like to generate..."
                  rows={3}
                />
              </div>
            )}

            <div className="flex gap-2">
              <Button 
                onClick={() => generateAIContent(aiContentType, aiContentType === 'custom' ? aiCustomPrompt : '')}
                disabled={aiGenerating || (aiContentType === 'custom' && !aiCustomPrompt)}
                className="bg-gradient-to-r from-purple-600 to-blue-600"
              >
                {aiGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" /> Generate
                  </>
                )}
              </Button>
            </div>

            {aiGeneratedContent && (
              <div className="space-y-3">
                <Label>Generated Content</Label>
                <div className="bg-stone-50 border rounded-lg p-4">
                  <p className="whitespace-pre-wrap text-sm">{aiGeneratedContent}</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => applyAIContent(aiGeneratedContent, aiContentType)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4 mr-2" /> Apply to Website
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      navigator.clipboard.writeText(aiGeneratedContent);
                      toast.success('Copied to clipboard!');
                    }}
                  >
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                  <Button variant="outline" onClick={() => generateAIContent(aiContentType, aiCustomPrompt)}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Regenerate
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebsiteBuilder;
